import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { X } from 'lucide-react'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import {
  getVendorLocations,
  IVendorLocation,
  IVendorProduct,
  IVendorUser,
  IVendorLocationDetails,
  removeVendorLocationProduct,
  removeVendorLocationUser,
  getVendorLocation,
} from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'
import { MessageBox } from '@/home/components/messages/MessageBox'
import { AssignModal } from '@/home/components/vendors/AssignModal'

export const LocationDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { editionId, festivalName, vendorId, locationId } = useParams()

  const [location, setLocation] = useState<IVendorLocation | null>(null)
  const [loading, setLoading] = useState(false)

  const [locationDetails, setLocationDetails] =
    useState<IVendorLocationDetails | null>(null)

  const [userCheckAll, setUserCheckAll] = useState(false)
  const [userCheck, setUserCheck] = useState<string[]>([])
  const [productCheckAll, setProductCheckAll] = useState(false)
  const [productCheck, setProductCheck] = useState<string[]>([])

  const [assignUserOpen, setAssignUserOpen] = useState(false)
  const [assignProductOpen, setAssignProductOpen] = useState(false)

  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'user' | 'product'
    id: string
    name: string
  } | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (msg: string) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(msg)
  }

  const fetchLocationDetails = useCallback(async () => {
    if (!vendorId || !locationId) return
    setLoading(true)
    try {
      const { data } = await getVendorLocation(vendorId, locationId)
      setLocationDetails(data)
    } catch (e) {
      if (e instanceof Error) handleError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId, locationId])

  const fetchLocation = useCallback(async () => {
    if (!vendorId || !locationId) return
    setLoading(true)
    try {
      const { data } = await getVendorLocations(vendorId)
      const loc = data.find((l) => l.id === locationId)
      if (loc) setLocation(loc)
    } catch (e) {
      if (e instanceof Error) handleError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId, locationId])

  useEffect(() => {
    fetchLocation()
    fetchLocationDetails()
  }, [fetchLocation, fetchLocationDetails])

  const handleRemoveUser = async (userId: string) => {
    if (!vendorId || !locationId) return
    try {
      await removeVendorLocationUser(vendorId, locationId, userId)
      fetchLocationDetails()
      setUserCheck((prev) => prev.filter((id) => id !== userId))
    } catch (e) {
      if (e instanceof Error) handleError(e.message)
    }
  }

  const handleRemoveProduct = async (productId: string) => {
    if (!vendorId || !locationId) return
    try {
      await removeVendorLocationProduct(vendorId, locationId, productId)
      fetchLocationDetails()
      setProductCheck((prev) => prev.filter((id) => id !== productId))
    } catch (e) {
      if (e instanceof Error) handleError(e.message)
    }
  }

  // User table
  const handleUserSelectAll = () => {
    setUserCheckAll(!userCheckAll)
    setUserCheck(
      !userCheckAll ? locationDetails?.users.map((u) => u.id) || [] : [],
    )
  }

  const handleUserClick = (id: string, checked: boolean) => {
    setUserCheck(
      checked ? [...userCheck, id] : userCheck.filter((i) => i !== id),
    )
  }

  const userColumns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleUserSelectAll}
          checked={userCheckAll}
        />
      ),
      accessor: '',
      render: (row: IVendorUser) => (
        <Checkbox
          onCheckedChange={(checked: boolean) =>
            handleUserClick(row.id, checked)
          }
          checked={userCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
    },
    {
      title: 'Phone',
      accessor: 'phone',
      sortable: true,
      searchable: true,
    },
    {
      title: '',
      accessor: '',
      render: (row: IVendorUser) =>
        userCheck.includes(row.id) ? (
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-monochrome-5/70 text-monochrome-1 hover:bg-red-500/30 hover:text-red-400 transition-colors cursor-pointer"
            onClick={() =>
              setDeleteConfirm({ type: 'user', id: row.id, name: row.name })
            }
          >
            <X className="h-4 w-4" />
          </button>
        ) : null,
    },
  ]

  // Product table
  const handleProductSelectAll = () => {
    setProductCheckAll(!productCheckAll)
    setProductCheck(
      !productCheckAll ? locationDetails?.products.map((p) => p.id) || [] : [],
    )
  }

  const handleProductClick = (id: string, checked: boolean) => {
    setProductCheck(
      checked ? [...productCheck, id] : productCheck.filter((i) => i !== id),
    )
  }

  const productColumns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleProductSelectAll}
          checked={productCheckAll}
        />
      ),
      accessor: '',
      render: (row: IVendorProduct) => (
        <Checkbox
          onCheckedChange={(checked: boolean) =>
            handleProductClick(row.id, checked)
          }
          checked={productCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Image',
      accessor: 'photo',
      render: (row: IVendorProduct) =>
        row.imageLink ? (
          <img
            src={row.imageLink}
            alt={row.name}
            className="h-10 w-10 rounded-md object-cover"
          />
        ) : (
          <div className="h-10 w-10 rounded-md bg-monochrome-3-60" />
        ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
    },
    {
      title: 'Tag',
      accessor: 'tags',
      searchable: true,
      render: (row: IVendorProduct) => <span>{row.tags.join(', ')}</span>,
    },
    {
      title: 'Price',
      accessor: 'price',
      sortable: true,
      render: (row: IVendorProduct) => <span>${row.price}</span>,
    },
    {
      title: '',
      accessor: '',
      render: (row: IVendorProduct) =>
        productCheck.includes(row.id) ? (
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-monochrome-5/70 text-monochrome-1 hover:bg-red-500/30 hover:text-red-400 transition-colors cursor-pointer"
            onClick={() =>
              setDeleteConfirm({
                type: 'product',
                id: row.id,
                name: row.name,
              })
            }
          >
            <X className="h-4 w-4" />
          </button>
        ) : null,
    },
  ]

  return (
    <>
      <Header pageTitle={location?.name || 'Location Details'} />
      {loading && <Loader />}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="secondary"
            className="border border-monochrome-120"
            onClick={() =>
              navigate(`/vendors/${festivalName}/${editionId}/${vendorId}`)
            }
          >
            <Icon
              icon="arrow-left"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-monochrome-1 text-center mb-6">
            {location?.name}
          </h1>
          <div className="w-40" />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Users Card */}
          <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg flex flex-col">
            <PageTitle title="Users" icon="Vendor Entity" />
            <div className="flex-1 max-h-[calc(100svh-400px)] overflow-y-auto">
              {locationDetails?.users.length ? (
                <Table
                  className="[&_th]:!whitespace-nowrap"
                  columns={userColumns}
                  data={locationDetails?.users || []}
                  isCheck={userCheck}
                />
              ) : (
                <div className="flex items-center justify-center py-20 text-monochrome-2">
                  No Users data available
                </div>
              )}
            </div>
            <Button
              variant="primary"
              className="w-full mt-4"
              onClick={() => setAssignUserOpen(true)}
            >
              Assign User
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              />
            </Button>
          </Card>

          {/* Products Card */}
          <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg flex flex-col">
            <PageTitle title="Products" icon="Vendor Entity" />
            <div className="flex-1 max-h-[calc(100svh-400px)] overflow-y-auto">
              {locationDetails?.products.length ? (
                <Table
                  className="[&_th]:!whitespace-nowrap"
                  columns={productColumns}
                  data={locationDetails?.products || []}
                  isCheck={productCheck}
                />
              ) : (
                <div className="flex items-center justify-center py-20 text-monochrome-2">
                  No Products data available
                </div>
              )}
            </div>
            <Button
              variant="primary"
              className="w-full mt-4"
              onClick={() => setAssignProductOpen(true)}
            >
              Assign Products
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              />
            </Button>
          </Card>
        </div>
      </div>

      <AssignModal
        open={assignUserOpen}
        onOpenChange={setAssignUserOpen}
        vendorId={vendorId!}
        locationId={locationId!}
        type="user"
        assignedIds={locationDetails?.users.map((u) => u.id) || []}
        onSuccess={fetchLocationDetails}
        onError={handleError}
      />

      <AssignModal
        open={assignProductOpen}
        onOpenChange={setAssignProductOpen}
        vendorId={vendorId!}
        locationId={locationId!}
        type="product"
        assignedIds={locationDetails?.products.map((p) => p.id) || []}
        onSuccess={fetchLocationDetails}
        onError={handleError}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={!!deleteConfirm}
        setDeleteConfirmationModal={(show) => {
          if (!show) setDeleteConfirm(null)
        }}
        deleteFunc={() => {
          if (deleteConfirm?.type === 'user') handleRemoveUser(deleteConfirm.id)
          else if (deleteConfirm?.type === 'product')
            handleRemoveProduct(deleteConfirm.id)
        }}
        deleteTitle={deleteConfirm?.type === 'user' ? 'User' : 'Product'}
        deletedItem={deleteConfirm?.name}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
